"use client";

/**
 * Email-verification block (PANDA-emailVerificationGate).
 *
 * Email/password users verify at signup: we hold them behind this modal —
 * which covers the whole app and is intentionally NOT dismissible — until
 * they click the magic link. It opens in three situations:
 *
 *   1. On mount, when the signed-in account is email-only and still
 *      unverified (a returning user who never finished verifying, or the
 *      first paint right after signup). Driven by /api/auth/verification-status.
 *   2. From the `panda:email-verification-required` window event, dispatched
 *      by the signup modal the instant an account is created (so the address
 *      shows without waiting for the status fetch).
 *   3. As a backstop, from any credit-spending API that returns 403 with
 *      `error: "email_verification_required"` — the fetch interceptor below
 *      catches those and dispatches the same event.
 *
 * Because that interceptor wraps `window.fetch` for the WHOLE page, it also
 * owns what happens when a third-party beacon (the Affonso referral pixel, the
 * analytics pixels) can't be reached — see the catch inside it.
 *
 * Social-login users (Google / Facebook / LinkedIn) are exempt — their email
 * is verified by the provider, so `must_verify` is always false for them.
 *
 * Auto-closes when the user verifies: we poll /api/auth/verification-status
 * (which reads our user_credits.email_verified_at column, NOT Supabase's
 * auto-filled email_confirmed_at) and reload the moment it flips, even if the
 * magic link was opened on a different device.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { CheckCircle2, Loader2, LogOut, Mail, RefreshCw } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { isValidEmailStructure } from "@/lib/validation/email";
import {
  resetAnalytics,
  trackEmailVerificationRequired,
  trackThirdPartyBeaconFailed,
} from "@/lib/analytics";
import {
  thirdPartyBeaconFailureResponse,
  thirdPartyBeaconHost,
} from "@/lib/observability/third-party-beacon-fetch";
import { clearTrialCheckoutSettled } from "@/lib/billing/trial-checkout-settled";
import { setModalOpen, MODAL_IDS } from "@/lib/ui/modal-coordinator";

interface GateDetail {
  email: string | null;
  // Carried by the 403 backstop / signup dispatch for analytics; no longer
  // shown in copy now that the block fires before any credit can be spent.
  consumed?: number;
}

declare global {
  interface WindowEventMap {
    "panda:email-verification-required": CustomEvent<GateDetail>;
  }
}

const fontTight = "font-[family-name:var(--font-inter-tight),sans-serif]";

/**
 * Hosts already reported this page load. A blocked beacon typically retries,
 * and one telemetry event per host is all the signal there is.
 */
const reportedBeaconHosts = new Set<string>();

/** Never throws: observability must not break the flow it observes. */
function reportBeaconFailure(host: string, err: unknown) {
  if (reportedBeaconHosts.has(host)) return;
  reportedBeaconHosts.add(host);
  try {
    trackThirdPartyBeaconFailed({
      host,
      reason: err instanceof Error ? err.message : String(err),
    });
  } catch {
    // Swallowed by design.
  }
}

export function EmailVerificationGateModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<
    "idle" | "sent" | "rate_limited" | "error"
  >("idle");
  const [resendError, setResendError] = useState<string | null>(null);
  // "change" mode swaps the resend UI for an input so a user who signed up
  // with a throwaway/typo'd address can move to a real inbox — verification
  // is impossible otherwise and they'd be stuck behind the block forever.
  const [mode, setMode] = useState<"default" | "change">("default");
  const [newEmail, setNewEmail] = useState("");
  const [changing, setChanging] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const verifiedRef = useRef(false);
  // Fire the "block shown" analytics once per open, no matter which trigger
  // (mount check, signup dispatch, or 403 backstop) opened it.
  const trackedRef = useRef(false);

  // Report open state to the global modal coordinator so lower-priority
  // modals (exit-intent survey, credit/upgrade walls) stand down while
  // this blocking gate is up instead of stacking on top of it (PANDA-1158).
  useEffect(() => {
    setModalOpen(MODAL_IDS.emailGate, open);
    if (!open) {
      trackedRef.current = false;
    } else if (!trackedRef.current) {
      trackedRef.current = true;
      trackEmailVerificationRequired(0, email);
    }
    return () => setModalOpen(MODAL_IDS.emailGate, false);
  }, [open, email]);

  const openWith = useCallback((nextEmail: string | null) => {
    setEmail(nextEmail);
    setResendStatus("idle");
    setResendError(null);
    // Always reopen on the default (resend) view — a stale "change" state
    // from a previous open would otherwise show the input first.
    setMode("default");
    setNewEmail("");
    setChangeError(null);
    setOpen(true);
  }, []);

  // Self-activate for an already-signed-in, still-unverified email-only user
  // (returning days later, or the first paint after signup). Check the local
  // session first so logged-out marketing visitors never hit the endpoint.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = createClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session || cancelled) return;
        const res = await fetch("/api/auth/verification-status", {
          cache: "no-store",
        });
        if (!res.ok || cancelled) return;
        const body = await res.json().catch(() => null);
        if (body?.must_verify) openWith(body.email ?? null);
      } catch {
        // Transient — the 403 backstop / signup dispatch will still fire.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openWith]);

  // Listen for the global event from the signup modal and the 403 backstop.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onRequired = (e: CustomEvent<GateDetail>) => {
      openWith(e.detail?.email ?? null);
    };
    window.addEventListener("panda:email-verification-required", onRequired);
    return () => {
      window.removeEventListener(
        "panda:email-verification-required",
        onRequired,
      );
    };
  }, [openWith]);

  // One-time global fetch interceptor — every response with a 403 +
  // `error: "email_verification_required"` dispatches the gate event,
  // so individual call sites don't need to opt in. Re-entrant safe via
  // a window-level flag.
  useEffect(() => {
    if (typeof window === "undefined") return;
    type Patched = typeof window.fetch & { __pandaEmailGatePatched?: boolean };
    const original = window.fetch as Patched;
    if (original.__pandaEmailGatePatched) return;

    const patched: Patched = async (...args) => {
      let res: Response;
      try {
        res = await original(...args);
      } catch (err) {
        // This wrapper sits on the stack of EVERY fetch on the page, third-party
        // scripts included. When a fire-and-forget beacon fails — the Affonso
        // referral pixel POSTing a `?ref=` visit to api.affonso.io is the one
        // that bit us — the vendor script doesn't catch it, and the resulting
        // unhandled `TypeError: Failed to fetch` files as a crash inside the
        // email verification gate. Answer those with an inert
        // synthetic response — nothing we render reads them — and report the
        // outage as a product event. Everything else rethrows untouched, so
        // our own call sites keep exact fetch semantics.
        const beaconHost = thirdPartyBeaconHost(args[0]);
        if (!beaconHost) throw err;
        reportBeaconFailure(beaconHost, err);
        return thirdPartyBeaconFailureResponse();
      }
      // Only inspect 403s — every other status short-circuits with no
      // body read so we don't break streaming / large responses.
      if (res.status === 403) {
        try {
          const cloned = res.clone();
          const ct = cloned.headers.get("content-type") ?? "";
          if (ct.includes("application/json")) {
            const body = await cloned.json().catch(() => null);
            if (body?.error === "email_verification_required") {
              window.dispatchEvent(
                new CustomEvent<GateDetail>(
                  "panda:email-verification-required",
                  { detail: { email: body.email ?? null } },
                ),
              );
            }
          }
        } catch {
          // Body already consumed / unreadable — ignore.
        }
      }
      return res;
    };
    patched.__pandaEmailGatePatched = true;
    window.fetch = patched;
    // Intentionally NOT restoring on unmount — this modal lives at the
    // app root, so unmount means navigation away from the entire app.
    // Re-attaching the patch on every modal remount would compound.
  }, []);

  // Watch for the user verifying — close the modal automatically the
  // moment our user_credits.email_verified_at column flips. We can't
  // use Supabase's email_confirmed_at here: it's auto-filled at signup
  // when "Confirm email" is disabled in the dashboard (Aaron's setup),
  // so it's true from second one. /api/auth/verification-status reads
  // the truthy column.
  useEffect(() => {
    if (!open) return;
    const supabase = createClient();
    let cancelled = false;

    const close = () => {
      if (cancelled || verifiedRef.current) return;
      verifiedRef.current = true;
      setOpen(false);
      // Page-level reconcile: a refresh ensures the user_credits row,
      // gate state, and in-flight UIs all pick up the verified status
      // without us having to plumb a "verified" flag through.
      if (typeof window !== "undefined") {
        // Signal to the project page that this reload follows a fresh
        // verification — picked up by the pending-prompt resume effect
        // so the build that triggered the gate auto-continues instead
        // of dropping the user back on an empty composer.
        try {
          sessionStorage.setItem("panda:just-verified", "1");
        } catch {}
        window.location.reload();
      }
    };

    const checkOnce = async () => {
      try {
        const res = await fetch("/api/auth/verification-status", {
          cache: "no-store",
        });
        if (!res.ok) return;
        const body = await res.json().catch(() => null);
        if (body?.verified) close();
      } catch {
        // transient
      }
    };

    void checkOnce();

    // The Supabase auth listener fires when our /auth/confirm landing
    // page completes the magic-link exchange (PKCE refresh / OTP verify
    // both produce a TOKEN_REFRESHED or USER_UPDATED event in the
    // originating tab). Run a one-shot status poll on any auth change.
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      void checkOnce();
    });

    // Backstop poll every 5s for cross-device verification.
    const id = setInterval(checkOnce, 5000);

    return () => {
      cancelled = true;
      clearInterval(id);
      sub.subscription.unsubscribe();
    };
  }, [open]);

  const handleResend = useCallback(async () => {
    setResending(true);
    setResendStatus("idle");
    setResendError(null);
    try {
      const res = await fetch("/api/auth/resend-verification", {
        method: "POST",
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setResendStatus("rate_limited");
        setResendError(body.message ?? "Try again in a minute.");
      } else if (!res.ok) {
        setResendStatus("error");
        setResendError(
          body.message ?? "Couldn't resend the verification email.",
        );
      } else if (body.already_verified) {
        // Edge case: block opened with stale state. Close modal.
        setOpen(false);
        if (typeof window !== "undefined") window.location.reload();
      } else {
        setResendStatus("sent");
      }
    } catch {
      setResendStatus("error");
      setResendError("Network blip — try again.");
    } finally {
      setResending(false);
    }
  }, []);

  const handleChangeEmail = useCallback(async () => {
    const candidate = newEmail.trim().toLowerCase();
    // Instant shape feedback; the server re-validates (incl. real-TLD).
    if (!isValidEmailStructure(candidate)) {
      setChangeError("Enter a valid email address.");
      return;
    }
    setChanging(true);
    setChangeError(null);
    try {
      const res = await fetch("/api/auth/change-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: candidate }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setChangeError(
          body.message ?? "Too many attempts — wait a minute and try again.",
        );
      } else if (res.status === 409) {
        setChangeError(body.message ?? "That email already has an account.");
      } else if (res.status === 400) {
        setChangeError(
          body.error === "same_email"
            ? "That's already your email address."
            : (body.message ?? "Enter a valid email address."),
        );
      } else if (!res.ok) {
        setChangeError(
          body.message ?? "Couldn't update your email. Try again.",
        );
      } else if (body.already_verified) {
        // Verified elsewhere mid-flow — close + reconcile like resend.
        setOpen(false);
        if (typeof window !== "undefined") window.location.reload();
      } else {
        // Success: point the modal at the new address and surface the
        // "link sent" confirmation. Verification polling keeps running, so
        // the modal auto-closes once they open the link in the new inbox.
        setEmail(candidate);
        setMode("default");
        setNewEmail("");
        if (body.warning === "rate_limited") {
          setResendStatus("rate_limited");
          setResendError(body.message ?? "Wait a minute, then tap Resend.");
        } else if (body.warning) {
          setResendStatus("error");
          setResendError(body.message ?? "Tap Resend to try the link again.");
        } else {
          setResendStatus("sent");
        }
      }
    } catch {
      setChangeError("Network blip — try again.");
    } finally {
      setChanging(false);
    }
  }, [newEmail]);

  // Escape hatch: the block is non-dismissible, so without this a user who
  // wants to sign into a DIFFERENT (e.g. already-verified) account would be
  // trapped. Sign out, reset analytics, and hard-navigate home so the app
  // re-reads auth from a clean, logged-out state.
  const handleLogout = useCallback(async () => {
    setLoggingOut(true);
    try {
      // `local` scope = drop this device's session (we're switching accounts,
      // not killing the user's sessions everywhere).
      await createClient().auth.signOut({ scope: "local" });
      resetAnalytics();
    } catch {
      // Best-effort — the unconditional cookie clear below is what actually
      // guarantees the logout takes effect.
    }
    // Unconditional, and outside the try above: this is the app's THIRD
    // sign-out and the only one that does not go through lib/auth/logout.ts, so
    // the clear that helper performs has to be repeated here. It matters most
    // exactly here — this hatch exists to switch accounts, and it reloads in the
    // SAME tab, where sessionStorage survives. Without it the next account's $1
    // trial checkout would bounce with nothing said. See
    // lib/billing/trial-checkout-settled.ts.
    clearTrialCheckoutSettled();
    // The Supabase SDK SKIPS clearing its auth cookie when the /logout revoke
    // call fails with a transient (non-401/403/404) error — and signOut then
    // resolves with `{ error }` rather than throwing, so the catch above never
    // runs. The surviving cookie would silently re-trap the user on the reload
    // below. So expire the Supabase auth cookies ourselves; createBrowserClient
    // stores them client-readable under the `sb-` prefix, so document.cookie
    // can clear them (including the chunked `…auth-token.0/.1` variants).
    try {
      if (typeof document !== "undefined") {
        for (const part of document.cookie.split(";")) {
          const name = part.split("=")[0]?.trim();
          if (name && name.startsWith("sb-")) {
            document.cookie = `${name}=; path=/; max-age=0`;
          }
        }
      }
    } catch {
      // document.cookie unavailable — fall through to the reload regardless.
    }
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  }, []);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Intentionally NOT dismissible — no X, no backdrop close. The user must
  // verify before entering the app. They can still open their inbox in a new
  // browser tab; clicking the magic link there flips our column and the poll
  // above closes this modal automatically.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm dark:bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="panda-verify-title"
    >
      <div
        className={`relative flex w-full max-w-115 flex-col items-center rounded-3xl bg-white px-5 pb-7 pt-7 text-center shadow-2xl sm:px-9 sm:pb-9 dark:bg-[#111522] ${fontTight}`}
      >
        {/* Mascot — present but small, so the modal reads as a functional
            step rather than a celebration. */}
        <Image
          src="/panda-lockup.png"
          alt="Panda"
          width={448}
          height={448}
          priority
          className="h-16 w-16 rounded-full object-cover object-left"
        />

        <h2
          id="panda-verify-title"
          className="mt-4 font-[family-name:var(--font-rubik),sans-serif] text-xl font-medium leading-7 text-[#1A1A1B] dark:text-[#f1f5f9] sm:text-[22px] sm:leading-8"
        >
          {mode === "default"
            ? "You're in. Let's build your app."
            : "Use a different email"}
        </h2>

        {mode === "default" ? (
          <>
            <p className="mt-2 text-sm leading-5 text-[#6B7280] dark:text-[#94a3b8]">
              Check your email and click the magic link. Your app could be live
              in minutes.
            </p>

            {/* Email pill — the exact address we mailed, front and centre. */}
            <div className="mt-3 inline-flex max-w-full items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3.5 py-1.5 dark:border-white/[0.08] dark:bg-white/[0.04]">
              <Mail className="h-3.5 w-3.5 shrink-0 text-[#9CA3AF] dark:text-[#94a3b8]" />
              <span className="truncate text-sm font-medium text-[#1A1A1B] dark:text-[#f1f5f9]">
                {email ?? "your inbox"}
              </span>
            </div>

            {/* Resend status — sits right above the action so it reads as
                feedback for the button. */}
            {resendStatus === "sent" && (
              <p className="mt-4 flex items-center justify-center gap-1.5 text-[13px] font-medium text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" />
                New link sent — check your inbox.
              </p>
            )}
            {resendStatus === "rate_limited" && resendError && (
              <p className="mt-4 text-[13px] text-amber-700 dark:text-amber-300">
                {resendError}
              </p>
            )}
            {resendStatus === "error" && resendError && (
              <p className="mt-4 text-[13px] text-rose-600 dark:text-rose-300">
                {resendError}
              </p>
            )}

            {/* Primary action: resend the magic link. We deliberately don't
                deep-link to a webmail inbox — guessing the provider from the
                address domain is unreliable. The user opens their own inbox. */}
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-panda-gradient-success font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-white shadow-md shadow-emerald-900/25 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Resending…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Resend email
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setMode("change");
                setChangeError(null);
                setNewEmail("");
              }}
              className="mt-3 text-sm font-medium text-emerald-700 transition-colors hover:opacity-70 dark:text-emerald-400"
            >
              Wrong email?
            </button>

            <p className="mt-4 text-[12px] leading-4 text-[#9CA3AF] dark:text-[#64748b]">
              This window closes automatically once your email is verified.
            </p>

            {/* Always-available escape — signing into a different (e.g.
                verified) account must never require getting past this block. */}
            <div className="mt-4 w-full border-t border-zinc-100 pt-3 dark:border-white/[0.06]">
              <button
                type="button"
                onClick={handleLogout}
                disabled={loggingOut}
                className="inline-flex items-center justify-center gap-1.5 text-[13px] font-medium text-[#6B7280] transition-colors hover:opacity-70 disabled:opacity-60 dark:text-[#94a3b8]"
              >
                {loggingOut ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <LogOut className="h-3.5 w-3.5" />
                )}
                {loggingOut ? "Logging out…" : "Log out / use a different account"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm leading-5 text-[#6B7280] dark:text-[#94a3b8]">
              Enter the email you&apos;d like to use. We&apos;ll send a fresh
              verification link there.
            </p>

            <label htmlFor="panda-change-email" className="sr-only">
              New email address
            </label>
            <input
              id="panda-change-email"
              type="email"
              autoFocus
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                if (changeError) setChangeError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !changing) void handleChangeEmail();
              }}
              placeholder="you@example.com"
              className="mt-4 w-full rounded-xl border border-zinc-200 bg-white px-4 py-3 text-sm text-[#1A1A1B] placeholder:text-zinc-400 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20 dark:border-white/[0.08] dark:bg-white/[0.02] dark:text-[#f1f5f9]"
            />

            {changeError && (
              <p className="mt-2 w-full text-left text-[13px] text-rose-600 dark:text-rose-300">
                {changeError}
              </p>
            )}

            <button
              type="button"
              onClick={handleChangeEmail}
              disabled={changing || !newEmail.trim()}
              className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-panda-gradient-success font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 text-white shadow-md shadow-emerald-900/25 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {changing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Updating…
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  Update &amp; send link
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                setMode("default");
                setChangeError(null);
              }}
              className="mt-3 text-sm font-medium text-[#6B7280] transition-colors hover:opacity-70 dark:text-[#94a3b8]"
            >
              Back
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Helper for callers that fetch credit-spending endpoints. Inspects a
 * Response and, when it's a 403 with `error: "email_verification_required"`,
 * dispatches the global event the modal listens for. Returns true when
 * the gate fired so callers can short-circuit their normal error handling.
 */
export async function maybeHandleEmailVerificationGate(
  res: Response,
): Promise<boolean> {
  if (res.status !== 403) return false;
  let body: { error?: string; email?: string | null };
  try {
    body = await res.clone().json();
  } catch {
    return false;
  }
  if (body?.error !== "email_verification_required") return false;
  if (typeof window === "undefined") return true;
  window.dispatchEvent(
    new CustomEvent<GateDetail>("panda:email-verification-required", {
      detail: { email: body.email ?? null },
    }),
  );
  return true;
}
